import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCheck, X, MapPin, Briefcase, Calendar, DollarSign, Mail, Phone, GraduationCap } from 'lucide-react';
import { ChatBubble } from '@/components/chat/ChatBubble';
import { ChatInput } from '@/components/chat/ChatInput';
import { useAuth } from '@/contexts/AuthContext';

// Mock data - will be replaced with Convex queries
const mockMessages = [
  {
    id: '1',
    text: 'Hei! Takk for matchen!',
    sender: 'employer',
    timestamp: new Date(),
  },
  {
    id: '2',
    text: 'Hei! Takk i like måte :)',
    sender: 'seeker',
    timestamp: new Date(),
  },
];

// Mock candidate profiles - will be replaced with Convex
const candidateProfiles: Record<string, {
  name: string;
  title: string;
  location: string;
  email: string;
  phone: string;
  bio: string;
  image: string;
  experience: Array<{ title: string; company: string; period: string }>;
  education: Array<{ degree: string; school: string; period: string }>;
  skills: string[];
}> = {
  'm1': {
    name: 'Ola Nordmann',
    title: 'Senior Frontend Utvikler',
    location: 'Oslo, Norge',
    email: 'ola.nordmann@example.com',
    phone: '+47 123 45 678',
    image: '/ola.jpg',
    bio: 'Erfaren frontend-utvikler med lidenskap for brukeropplevelse og moderne webteknologi.',
    experience: [{ title: 'Senior Frontend Utvikler', company: 'Tech Solutions AS', period: '2020 - Nå' }],
    education: [{ degree: 'Master i Informatikk', school: 'Universitetet i Oslo', period: '2015 - 2018' }],
    skills: ['React', 'TypeScript', 'Node.js', 'Next.js', 'Tailwind CSS']
  },
  'm2': {
    name: 'Kari Hansen',
    title: 'DevOps Engineer',
    location: 'Bergen, Norge',
    email: 'kari.hansen@example.com',
    phone: '+47 987 65 432',
    image: '/kari.jpg',
    bio: 'Spesialist på cloud-infrastruktur og automatisering.',
    experience: [{ title: 'DevOps Engineer', company: 'Cloud Systems AS', period: '2019 - Nå' }],
    education: [{ degree: 'Bachelor i Dataingeniør', school: 'Universitetet i Bergen', period: '2015 - 2019' }],
    skills: ['Kubernetes', 'Docker', 'AWS', 'Terraform', 'CI/CD']
  },
  'm5': {
    name: 'Erik Johansen',
    title: 'Backend Developer',
    location: 'Oslo, Norge',
    email: 'erik.johansen@example.com',
    phone: '+47 456 78 901',
    image: '/profilbilde.png',
    bio: 'Backend-utvikler med fokus på skalerbare systemer og fintech.',
    experience: [{ title: 'Backend Developer', company: 'FinTech Solutions', period: '2018 - Nå' }],
    education: [{ degree: 'Master i Informatikk', school: 'NTNU', period: '2014 - 2018' }],
    skills: ['Node.js', 'PostgreSQL', 'Redis', 'Microservices', 'PCI-DSS']
  },
  'm6': {
    name: 'Nina Kristiansen',
    title: 'UI/UX Designer',
    location: 'Bergen, Norge',
    email: 'nina.kristiansen@example.com',
    phone: '+47 234 56 789',
    image: '/kari.jpg',
    bio: 'UI/UX designer med lidenskap for skandinavisk design.',
    experience: [{ title: 'Senior Designer', company: 'Design Studio', period: '2017 - Nå' }],
    education: [{ degree: 'Master i Design', school: 'KHiB', period: '2013 - 2017' }],
    skills: ['Figma', 'Adobe XD', 'User Research', 'Prototyping', 'Design Systems']
  },
  'm7': {
    name: 'Thomas Andreassen',
    title: 'Tech Lead',
    location: 'Oslo, Norge',
    email: 'thomas.andreassen@example.com',
    phone: '+47 345 67 890',
    image: '/ola.jpg',
    bio: 'Erfaren tech lead med fokus på team-utvikling.',
    experience: [{ title: 'Tech Lead', company: 'Enterprise Solutions', period: '2016 - Nå' }],
    education: [{ degree: 'Master i Informatikk', school: 'UiO', period: '2012 - 2016' }],
    skills: ['Leadership', 'Architecture', 'Java', 'Spring', 'Mentoring']
  }
};

// Mock match data - will be replaced with Convex
const getMockMatch = (matchId: string) => ({
  id: matchId,
  companyName: 'Acme AS',
  jobTitle: 'Frontend Utvikler',
  candidateName: candidateProfiles[matchId]?.name || 'Kandidat #1',
  isInfoShared: !!candidateProfiles[matchId],
});

// Mock job details mapping - will be replaced with Convex
const getJobDetails = (matchId: string) => {
  const jobs: Record<string, any> = {
    'm1': {
      title: 'Senior Frontend Utvikler',
      company: 'TechNorge AS',
      location: 'Oslo',
      type: 'Heltid',
      salary: '700 000 - 900 000 kr',
      posted: '2 timer siden',
      description: 'Vi søker en erfaren Senior Frontend Utvikler til vårt team i Oslo.',
      requirements: ['5+ års erfaring med React', 'TypeScript', 'Testing'],
      benefits: ['Fleksibel arbeidstid', 'Hjemmekontor', 'Faglig utvikling']
    },
    'm5': {
      title: 'Backend Developer',
      company: 'FinTech Norge',
      location: 'Oslo',
      type: 'Heltid',
      salary: '650 000 - 850 000 kr',
      posted: '6 timer siden',
      description: 'Vi søker en Backend Developer til vårt fintech-team. Du vil jobbe med betalingsløsninger og sikkerhet.',
      requirements: ['Node.js erfaring', 'PostgreSQL', 'Sikkerhet', 'PCI-DSS'],
      benefits: ['Konkurransedyktig lønn', 'Fleksibel arbeidstid', 'Faglig utvikling', 'Moderne kontor']
    }
  };
  return jobs[matchId] || jobs['m1'];
};

export default function ChatDetailPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState(mockMessages);
  
  const mockMatch = getMockMatch(matchId || 'm1');
  const mockCandidateProfile = candidateProfiles[matchId || 'm1'] || candidateProfiles['m1'];
  const mockJobDetails = getJobDetails(matchId || 'm1');
  
  const [isInfoShared, setIsInfoShared] = useState(mockMatch.isInfoShared);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const isSeeker = user?.role === 'seeker';
  const isEmployer = user?.role === 'employer';

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = (text: string) => {
    const newMessage = {
      id: Date.now().toString(),
      text: text,
      sender: isSeeker ? 'seeker' : 'employer',
      timestamp: new Date(),
    };
    
    setMessages([...messages, newMessage]);
    // TODO: Send message via Convex
    console.log('Sending message:', text);
  };

  const handleShareInfo = () => {
    // TODO: Update match status via Convex
    setIsInfoShared(true);
    console.log('Deler informasjon for match:', matchId);
  };

  return (
    <div style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* NavBar */}
      <div
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 h-16 backdrop-blur-lg"
        style={{
          backgroundColor: 'rgba(14, 14, 15, 0.95)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-lg hover:bg-white/5 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" style={{ color: 'var(--color-text-primary)' }} />
        </button>

        {/* Title */}
        <h1
          className="absolute left-1/2 transform -translate-x-1/2 font-semibold truncate max-w-[60%]"
          style={{
            fontSize: 'var(--font-size-heading-sm)',
            color: 'var(--color-text-primary)',
          }}
        >
          {mockJobDetails.title}
        </h1>

        {/* Share Info Button (only for seekers, hidden when info is shared) */}
        {isSeeker && !isInfoShared && (
          <button
            onClick={handleShareInfo}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-accent)',
              color: 'white',
            }}
          >
            <UserCheck className="w-5 h-5" />
            <span className="text-sm font-medium">Del min info</span>
          </button>
        )}

        {/* Empty space to balance layout when button is not shown */}
        {(!isSeeker || isInfoShared) && <div className="w-10" />}
      </div>

      {/* Company Info Bar (only for seekers) - Clickable */}
      {isSeeker && (
        <button
          onClick={() => setShowJobModal(true)}
          className="fixed top-16 left-0 right-0 z-40 px-4 py-3 text-center transition-all hover:bg-opacity-80"
          style={{
            backgroundColor: 'rgba(79, 70, 229, 0.1)',
            borderBottom: '1px solid rgba(79, 70, 229, 0.2)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Du chatter med{' '}
            <span className="font-semibold" style={{ color: 'var(--color-bg-accent)' }}>
              {mockMatch.companyName}
            </span>
            {' '}om stillingen{' '}
            <span className="font-semibold underline" style={{ color: 'var(--color-text-primary)' }}>
              {mockMatch.jobTitle}
            </span>
          </p>
        </button>
      )}

      {/* Anonymity Info Bar (only for employers when info is not shared) */}
      {isEmployer && !isInfoShared && (
        <div
          className="fixed top-16 left-0 right-0 z-40 px-4 py-3 text-center"
          style={{
            backgroundColor: 'rgba(251, 191, 36, 0.1)',
            borderBottom: '1px solid rgba(251, 191, 36, 0.2)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Kandidaten er anonym. Venter på at de deler sin informasjon...
          </p>
        </div>
      )}

      {/* Candidate Profile Card (only for employers when info IS shared) */}
      {isEmployer && isInfoShared && (
        <button
          onClick={() => setShowCandidateModal(true)}
          className="fixed top-16 left-0 right-0 z-40 px-4 py-4 text-left transition-colors w-full"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderBottom: '1px solid var(--color-border)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
          }}
        >
          <div className="flex items-start gap-4">
            {/* Profile Image */}
            <div 
              className="w-16 h-16 rounded-full flex-shrink-0 overflow-hidden"
              style={{ backgroundColor: 'var(--color-bg-accent)' }}
            >
              <img 
                src={mockCandidateProfile.image} 
                alt={mockCandidateProfile.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center" style="background-color: var(--color-bg-accent)">
                        <span style="color: white; font-size: 1.5rem; font-weight: bold;">
                          ${mockCandidateProfile.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                    `;
                  }
                }}
              />
            </div>

            {/* Profile Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg truncate" style={{ color: 'var(--color-text-primary)' }}>
                {mockCandidateProfile.name}
              </h3>
              <p className="text-sm truncate" style={{ color: 'var(--color-text-secondary)' }}>
                {mockCandidateProfile.title}
              </p>
              <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span>{mockCandidateProfile.location}</span>
                </div>
              </div>
            </div>

            {/* Contact Buttons (right side) */}
            <div className="flex flex-col gap-2">
              <a 
                href={`mailto:${mockCandidateProfile.email}`}
                className="flex items-center gap-1 px-3 py-2 rounded text-xs font-medium"
                style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}
              >
                <Mail className="w-4 h-4" />
                E-post
              </a>
              <a 
                href={`tel:${mockCandidateProfile.phone}`}
                className="flex items-center gap-1 px-3 py-2 rounded text-xs font-medium"
                style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}
              >
                <Phone className="w-4 h-4" />
                Ring
              </a>
            </div>
          </div>
        </button>
      )}

      {/* Chat Container */}
      <div 
        ref={chatContainerRef}
        className="p-4 space-y-4"
        style={{
          position: 'fixed',
          top: isSeeker ? '6rem' : (isEmployer && isInfoShared ? '10.5rem' : (isEmployer && !isInfoShared ? '6rem' : '4rem')),
          bottom: 'calc(4rem + 3px)',
          left: 0,
          right: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {messages.map((message) => (
          <ChatBubble
            key={message.id}
            message={message.text}
            isOwn={message.sender === (isSeeker ? 'seeker' : 'employer')}
            timestamp={message.timestamp}
          />
        ))}
      </div>

      {/* Input */}
      <ChatInput onSend={handleSendMessage} />

      {/* Job Details Modal */}
      {showJobModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={() => setShowJobModal(false)}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          />

          {/* Modal */}
          <div
            className="relative w-full h-[90vh] overflow-y-auto rounded-t-3xl shadow-xl"
            style={{ backgroundColor: 'var(--color-bg-primary)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="sticky top-0 z-10 flex items-center justify-between p-6 border-b"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
              }}
            >
              <h2
                className="text-xl font-semibold"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {mockJobDetails.title}
              </h2>
              <button
                onClick={() => setShowJobModal(false)}
                className="p-2 rounded-full hover:bg-white/5 transition-colors"
              >
                <X className="w-6 h-6" style={{ color: 'var(--color-text-secondary)' }} />
              </button>
            </div>

            {/* Content */}
            <div 
              className="p-6 space-y-6"
              style={{ backgroundColor: 'var(--color-bg-primary)' }}
            >
              {/* Company Info */}
              <div>
                <h3
                  className="text-2xl font-bold mb-2"
                  style={{ color: 'var(--color-bg-accent)' }}
                >
                  {mockJobDetails.company}
                </h3>
                <div className="flex flex-wrap gap-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{mockJobDetails.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    <span>{mockJobDetails.type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    <span>{mockJobDetails.salary}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{mockJobDetails.posted}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h4
                  className="text-lg font-semibold mb-2"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Om stillingen
                </h4>
                <p
                  className="leading-relaxed"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {mockJobDetails.description}
                </p>
              </div>

              {/* Requirements */}
              <div>
                <h4
                  className="text-lg font-semibold mb-2"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Krav
                </h4>
                <ul className="space-y-2">
                  {mockJobDetails.requirements.map((req, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      <span style={{ color: 'var(--color-bg-accent)' }}>•</span>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Benefits */}
              <div>
                <h4
                  className="text-lg font-semibold mb-2"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Vi tilbyr
                </h4>
                <ul className="space-y-2">
                  {mockJobDetails.benefits.map((benefit, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      <span style={{ color: 'var(--color-bg-accent)' }}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div
              className="sticky bottom-0 p-6 border-t"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
              }}
            >
              <button
                onClick={() => setShowJobModal(false)}
                className="w-full py-3 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-accent)',
                  color: 'white',
                }}
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Candidate CV Modal (for employers) */}
      {showCandidateModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={() => setShowCandidateModal(false)}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          />

          {/* Modal */}
          <div
            className="relative w-full h-[90vh] overflow-y-auto rounded-t-3xl shadow-xl"
            style={{ backgroundColor: 'var(--color-bg-primary)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Profile Image */}
            <div
              className="sticky top-0 z-10 border-b"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
              }}
            >
              <div className="relative">
                {/* Background gradient */}
                <div 
                  className="h-32"
                  style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  }}
                />
                
                {/* Profile Image */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
                  <div className="w-24 h-24 rounded-full border-4 overflow-hidden"
                    style={{ 
                      borderColor: 'var(--color-bg-primary)',
                      backgroundColor: 'var(--color-bg-accent)'
                    }}
                  >
                    <img 
                      src={mockCandidateProfile.image} 
                      alt={mockCandidateProfile.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center" style="background-color: var(--color-bg-accent)">
                            <span style="color: white; font-size: 2rem; font-weight: bold;">
                              ${mockCandidateProfile.name.split(' ').map((n: string) => n[0]).join('')}
                            </span>
                          </div>
                        `;
                      }}
                    />
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setShowCandidateModal(false)}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>

              {/* Name and Title */}
              <div className="text-center pt-14 pb-6 px-6">
                <h2
                  className="text-2xl font-bold mb-1"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {mockCandidateProfile.name}
                </h2>
                <p
                  className="text-lg mb-3"
                  style={{ color: 'var(--color-bg-accent)' }}
                >
                  {mockCandidateProfile.title}
                </p>
                <div className="flex items-center justify-center gap-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{mockCandidateProfile.location}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div 
              className="p-6 space-y-6"
              style={{ backgroundColor: 'var(--color-bg-primary)' }}
            >
              {/* Contact Info */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Kontaktinformasjon
                </h3>
                <div className="space-y-2">
                  <a 
                    href={`mailto:${mockCandidateProfile.email}`}
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-white/5 transition-colors"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    <Mail className="w-5 h-5" style={{ color: 'var(--color-bg-accent)' }} />
                    <span>{mockCandidateProfile.email}</span>
                  </a>
                  <a 
                    href={`tel:${mockCandidateProfile.phone}`}
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-white/5 transition-colors"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    <Phone className="w-5 h-5" style={{ color: 'var(--color-bg-accent)' }} />
                    <span>{mockCandidateProfile.phone}</span>
                  </a>
                </div>
              </div>

              {/* Bio */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Om meg
                </h3>
                <p
                  className="leading-relaxed"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {mockCandidateProfile.bio}
                </p>
              </div>

              {/* Experience */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <Briefcase className="w-5 h-5" style={{ color: 'var(--color-bg-accent)' }} />
                  Erfaring
                </h3>
                <div className="space-y-4">
                  {mockCandidateProfile.experience.map((exp, index) => (
                    <div key={index} className="pl-4 border-l-2" style={{ borderColor: 'var(--color-bg-accent)' }}>
                      <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {exp.title}
                      </h4>
                      <p className="text-sm" style={{ color: 'var(--color-bg-accent)' }}>
                        {exp.company}
                      </p>
                      <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>
                        {exp.period}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {exp.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Education */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <GraduationCap className="w-5 h-5" style={{ color: 'var(--color-bg-accent)' }} />
                  Utdanning
                </h3>
                <div className="space-y-3">
                  {mockCandidateProfile.education.map((edu, index) => (
                    <div key={index} className="pl-4 border-l-2" style={{ borderColor: 'var(--color-bg-accent)' }}>
                      <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {edu.degree}
                      </h4>
                      <p className="text-sm" style={{ color: 'var(--color-bg-accent)' }}>
                        {edu.school}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {edu.period}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Skills */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Ferdigheter
                </h3>
                <div className="flex flex-wrap gap-2">
                  {mockCandidateProfile.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: 'rgba(79, 70, 229, 0.1)',
                        color: 'var(--color-bg-accent)'
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              className="sticky bottom-0 p-6 border-t"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
              }}
            >
              <button
                onClick={() => setShowCandidateModal(false)}
                className="w-full py-3 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-accent)',
                  color: 'white',
                }}
              >
                Lukk profil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

